import { z } from "zod";
import { idSchema } from "./common.js";

/**
 * Phase 12g — input schemas for the `ai.scan*` procedures. Each scan
 * takes an ordered list of photo ids (R2 keys uploaded via
 * `photos.uploadUrl`); the procedure body composes the bake-off-
 * decided provider's invocation and returns the resulting
 * `SuggestionSet`.
 *
 * Multi-image input is first-class per R-AI-1a: `photoIds` is an
 * ordered list (overview-then-closeup capture order), passed through
 * to the provider verbatim. Single-shot scans carry one id; the
 * two-shot panel-scan fixture (`Architecture.md` §6) carries two.
 *
 * The procedures are *not* idempotent (per the Phase 12g spec): AI
 * calls are expensive, and a re-run with the same photo costs another
 * invocation. The Phase 13 capture flow does not retry automatically —
 * a user-initiated "scan again" is the only path to a duplicate call.
 */

/**
 * One scan request. Mirrors the domain `AiScanRequest` shape (Phase 8b)
 * with `photoIds` non-empty: a zero-photo scan has no input bytes for
 * the provider to read.
 */
/**
 * Phase 19c — an optional user-supplied panel identity hint
 * (manufacturer / series, e.g. "Square D" / "QO"). Threaded down to
 * `runPanelScanDirect` to seed the identity pass when the electrician
 * already knows the make. Both fields optional: a caller may supply only
 * one. Dead-but-wired in 19c (forwarded, ignored downstream); the
 * identity pass starts consuming it in 19e.
 */
export const panelIdentitySchema = z.object({
  manufacturer: z.string().optional(),
  series: z.string().optional(),
});
export type PanelIdentityInput = z.infer<typeof panelIdentitySchema>;

export const aiScanPanelInputSchema = z.object({
  photoIds: z.array(idSchema).min(1).max(8),
  panelIdentity: panelIdentitySchema.optional(),
});
export type AiScanPanelInput = z.infer<typeof aiScanPanelInputSchema>;

export const aiScanRoomInputSchema = z.object({
  photoIds: z.array(idSchema).min(1).max(8),
});
export type AiScanRoomInput = z.infer<typeof aiScanRoomInputSchema>;

export const aiScanNameplateInputSchema = z.object({
  photoIds: z.array(idSchema).min(1).max(4),
});
export type AiScanNameplateInput = z.infer<typeof aiScanNameplateInputSchema>;

/**
 * KAN-14 — async scan surface. The synchronous `ai.scan*` procedures
 * above held one HTTP request open for the whole provider call (~50s),
 * which is fragile on mobile radios and a memory hazard (the image
 * buffers stay pinned with no backpressure). `ai.submitScan` records a
 * job and returns immediately with a `jobId`; the client polls
 * `ai.scanStatus` until the job reaches a terminal status.
 *
 * The scan *kind* is explicit on submit (the sync surface encoded it in
 * the procedure name). `photoIds` keeps the R-AI-1a ordered, non-empty
 * contract; the `max(8)` ceiling covers the panel / room cases (the
 * nameplate close-up uses fewer).
 *
 * Like the sync procedures, `submitScan` is intentionally *not*
 * idempotent: re-submitting the same photos costs another invocation, so
 * an idempotency replay would mask a paid second scan as a free one. The
 * client renders an explicit "scan again" confirmation instead of
 * auto-retrying.
 */
export const aiScanKindSchema = z.enum([
  "panel-scan",
  "room-device-scan",
  "nameplate-ocr",
]);
export type AiScanKind = z.infer<typeof aiScanKindSchema>;

export const aiSubmitScanInputSchema = z.object({
  kind: aiScanKindSchema,
  photoIds: z.array(idSchema).min(1).max(8),
  /**
   * Phase 19c — optional panel-identity hint (manufacturer / series),
   * mirrored from `aiScanPanelInputSchema` for parity. Only meaningful for
   * `kind: "panel-scan"`; dead-but-wired through the request chain in 19c.
   */
  panelIdentity: panelIdentitySchema.optional(),
});
export type AiSubmitScanInput = z.infer<typeof aiSubmitScanInputSchema>;

export const aiScanStatusInputSchema = z.object({
  jobId: idSchema,
});
export type AiScanStatusInput = z.infer<typeof aiScanStatusInputSchema>;

/**
 * Job lifecycle the client polls: `queued` → `running` → terminal
 * `succeeded` (carries the `SuggestionSet` result) | `failed` (carries an
 * error code + message). Cancellation is client-side only (the user
 * leaving the analyzing screen) — there is no server-cancel arm in this
 * cut.
 */
export const aiScanJobStatusSchema = z.enum([
  "queued",
  "running",
  "succeeded",
  "failed",
]);
export type AiScanJobStatusName = z.infer<typeof aiScanJobStatusSchema>;
